import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ENGAGEMENT_TYPE_OPTIONS } from '../../lib/invitationsAPI';
import { BILLING_FREQUENCY_OPTIONS, FEE_TYPE_OPTIONS, fmtMoney } from '../../lib/servicesEngagementAPI';

const GST_RATE = 0.1;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Default billing frequency per engagement type (mirrors the service catalogue defaults).
const DEFAULT_FREQUENCY = {
  individual_tax_return: 'per_lodgement', business_tax_return: 'per_lodgement', bas_gst: 'quarterly', payroll_paygw: 'monthly',
  bookkeeping: 'monthly', asic_company_secretarial: 'annual', advisory: 'one_off', other: 'one_off',
};

export const emptyFeeLine = (type) => ({
  fee_amount: '', billing_frequency: DEFAULT_FREQUENCY[type] || 'one_off', fee_type: 'fixed', description: '',
});

export const initialEngagementFees = (types = []) => ({
  types, fees: Object.fromEntries(types.map((t) => [t, emptyFeeLine(t)])),
});

// -> { engagement_types, fee_lines } for the API (blank amount => null, set later).
export const toFeePayload = ({ types, fees }) => ({
  engagement_types: types,
  fee_lines: types.map((t) => ({
    engagement_type: t,
    description: t === 'other' ? (fees[t].description || null) : null,
    fee_amount: fees[t].fee_amount === '' ? null : Number(fees[t].fee_amount),
    billing_frequency: fees[t].billing_frequency,
    fee_type: fees[t].fee_type,
    gst_applicable: true,
  })),
});

const lineAmounts = (line) => {
  const fee = r2(line.fee_amount);
  const gst = r2(fee * GST_RATE);
  return { fee, gst, total: r2(fee + gst) };
};

const FeeRow = ({ type, label, line, onChange }) => {
  const { gst, total } = lineAmounts(line);
  const set = (k) => (v) => onChange({ ...line, [k]: v });
  return (
    <div className="grid grid-cols-12 gap-2 items-center border-t py-2" data-testid={`fee-row-${type}`}>
      <div className="col-span-12 md:col-span-3 text-sm font-medium text-foreground">
        {label}
        {type === 'other' && (
          <Input className="mt-1 h-8" placeholder="Describe the service" value={line.description}
            onChange={(e) => set('description')(e.target.value)} data-testid={`fee-description-${type}`} />
        )}
      </div>
      <Input className="col-span-4 md:col-span-2 h-9" type="number" min="0" step="0.01" placeholder="Ex GST"
        value={line.fee_amount} onChange={(e) => set('fee_amount')(e.target.value)} data-testid={`fee-amount-${type}`} />
      <div className="col-span-4 md:col-span-2">
        <Select value={line.billing_frequency} onValueChange={set('billing_frequency')}>
          <SelectTrigger className="h-9" data-testid={`fee-frequency-${type}`}><SelectValue /></SelectTrigger>
          <SelectContent>{BILLING_FREQUENCY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-4 md:col-span-2">
        <Select value={line.fee_type} onValueChange={set('fee_type')}>
          <SelectTrigger className="h-9" data-testid={`fee-type-${type}`}><SelectValue /></SelectTrigger>
          <SelectContent>{FEE_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-6 md:col-span-1 text-sm text-muted-foreground text-right" data-testid={`fee-gst-${type}`}>{fmtMoney(gst)}</div>
      <div className="col-span-6 md:col-span-2 text-sm font-medium text-foreground text-right" data-testid={`fee-total-${type}`}>{fmtMoney(total)}</div>
    </div>
  );
};

const Totals = ({ types, fees }) => {
  const sum = types.reduce((acc, t) => {
    const a = lineAmounts(fees[t]);
    return { fee: acc.fee + a.fee, gst: acc.gst + a.gst, total: acc.total + a.total };
  }, { fee: 0, gst: 0, total: 0 });
  return (
    <div className="flex flex-wrap justify-end gap-6 border-t pt-3 text-sm" data-testid="fee-grand-total">
      <span className="text-muted-foreground">Ex GST <b className="text-foreground" data-testid="fee-total-ex-gst">{fmtMoney(r2(sum.fee))}</b></span>
      <span className="text-muted-foreground">GST <b className="text-foreground" data-testid="fee-total-gst">{fmtMoney(r2(sum.gst))}</b></span>
      <span className="text-muted-foreground">Incl GST <b className="text-foreground" data-testid="fee-total-incl-gst">{fmtMoney(r2(sum.total))}</b></span>
    </div>
  );
};

export const EngagementFeesEditor = ({ value, onChange }) => {
  const { types, fees } = value;
  const toggle = (t, checked) => {
    const nextTypes = checked ? ENGAGEMENT_TYPE_OPTIONS.map((o) => o.value).filter((v) => v === t || types.includes(v))
      : types.filter((x) => x !== t);
    onChange({ types: nextTypes, fees: { ...fees, [t]: fees[t] || emptyFeeLine(t) } });
  };
  const labelOf = (t) => (ENGAGEMENT_TYPE_OPTIONS.find((o) => o.value === t) || {}).label || t;

  return (
    <div className="space-y-3" data-testid="engagement-fees-editor">
      <Label>Engagement types * <span className="text-xs text-muted-foreground font-normal">(select one or more)</span></Label>
      <div className="flex flex-wrap gap-2">
        {ENGAGEMENT_TYPE_OPTIONS.map((o) => (
          <label key={o.value} className={`flex items-center gap-2 border rounded-full px-3 py-1.5 text-sm cursor-pointer transition-colors ${types.includes(o.value) ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}`}>
            <Checkbox checked={types.includes(o.value)} onCheckedChange={(c) => toggle(o.value, !!c)} data-testid={`engagement-type-${o.value}`} />
            {o.label}
          </label>
        ))}
      </div>
      {types.length > 0 && (
        <div className="border rounded-lg p-3" data-testid="fee-lines">
          <div className="hidden md:grid grid-cols-12 gap-2 text-xs text-muted-foreground pb-1">
            <span className="col-span-3">Service</span><span className="col-span-2">Fee (ex GST)</span>
            <span className="col-span-2">Billing</span><span className="col-span-2">Fee type</span>
            <span className="col-span-1 text-right">GST</span><span className="col-span-2 text-right">Total</span>
          </div>
          {types.map((t) => (
            <FeeRow key={t} type={t} label={labelOf(t)} line={fees[t]}
              onChange={(line) => onChange({ types, fees: { ...fees, [t]: line } })} />
          ))}
          <Totals types={types} fees={fees} />
          <p className="text-xs text-muted-foreground mt-2">Amounts can be left blank and set later. Fees are not shown to the client in the invitation.</p>
        </div>
      )}
    </div>
  );
};
